import {
	GalleryItem,
	Settings,
	CoverItem,
	ProductInfo,
	PredefinedConfiguration,
	RangeSettings,
	MissingProductItem,
} from 'sofa-reborn-react';
import { ThunkResult, AsyncThunkResult, RootState } from '..';
import { noArticlesInplanner, setFakeData } from './actions';
import { setItemsInModuleSwiper, setModuleItemsChanged } from '../swiper/actions';

import {
	parseGroups,
	parseProductData,
	getPredefinedConfigurations,
	getRangeSettings,
	parseModelData,
} from '../../util/ProductParser';
import { parseModuleSwiperItems } from '../../util/SofaFamilyParser';
import { ProductData } from '@ikea-aoa/component-view-3d';
import { StoreService } from '@inter-ikea-kompis/services';
import { fetchConfigPending, fetchConfigSuccess, fetchConfigFailure } from '../products';
import { localeService, UrlService } from '../../services';
import { setSelectedCover, setCoverItems, setCoverOnlyItems } from '../cover';
import { setSettings } from '../settings';
import { setModels } from '../models';

import DataHandler from './utils/DataHandler';
import AssignUtil from './utils/AssignUtil';
import {
	ProductVariantParser,
	GalleryItemsParser,
	PredefinedProductInfoParser,
	ProductInfoParser,
	MissingProductsParser,
	CoverParser,
} from './parsers';

type IDataFetch = {
	dispatch;
	products: ProductData;
	productInfo: ProductInfo;
	state: RootState;
	rangeSettings: RangeSettings;
	config: unknown;
	useLocalIds: boolean;
	fetchedProducts;
};

export function fetchConfigThunk(): AsyncThunkResult {
	return async (dispatch, getState): Promise<void> => {
		const state = getState();
		const { settings, sofaRange } = state.settings;
		const { fakeData, disableBulkFetch, usePersistedConfig } = state.application;
		const locale = state.localization.locale as string;
		dispatch(fetchConfigPending());

		try {
			// Fetch the latest config specs
			const cmsConfigVersion = '2.0.1362';
			const dataHandler = new DataHandler();
			const [config, fetchedProducts] = await dataHandler.getData({
				cmsConfigVersion,
				settings,
				sofaRange,
				disableBulkFetch,
				usePersistedConfig,
			});

			const rangeSettings: RangeSettings = getRangeSettings(config);
			const useLocalIds = !!rangeSettings.useLocalIds?.includes(localeService.getCountryCode());

			const { products, productInfo } = await handleProduct(
				config,
				rangeSettings,
				locale,
				fakeData,
				useLocalIds,
				fetchedProducts,
				settings
			);

			await handleDataFetch({
				dispatch,
				products,
				productInfo,
				state,
				rangeSettings,
				config,
				useLocalIds,
				fetchedProducts,
			});
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error);
			dispatch(fetchConfigFailure(error));
		}
	};
}

async function handleProduct(
	config: unknown,
	rangeSettings: RangeSettings,
	locale: string,
	fakeData,
	useLocalIds: boolean,
	fetchedProducts,
	settings: Settings
): Promise<{ products: ProductData; productInfo: ProductInfo }> {
	const products: ProductData = parseProductData(
		config,
		rangeSettings,
		localeService.getCountryCode()
	);

	const productInfoParser = new ProductInfoParser();
	const productInfo: ProductInfo = await productInfoParser.parse(
		products,
		rangeSettings,
		locale,
		fakeData,
		useLocalIds,
		fetchedProducts,
		settings
	);
	AssignUtil.localIds(products, productInfo);
	AssignUtil.images(products, productInfo);
	return { products, productInfo };
}

async function handlePredefined(
	data: IDataFetch,
	galleryItems: GalleryItem[]
): Promise<{
	predefinedConfigurations: PredefinedConfiguration[];
	allPredefinedConfigurations: PredefinedConfiguration[];
}> {
	let predefinedConfigurations: PredefinedConfiguration[] = getPredefinedConfigurations(
		data.config
	);
	const allPredefinedConfigurations: PredefinedConfiguration[] = predefinedConfigurations.slice();
	const predefinedProductInfoParser = new PredefinedProductInfoParser();
	const predefinedProductInfo = predefinedProductInfoParser.parse(
		predefinedConfigurations,
		data.useLocalIds,
		data.fetchedProducts
	);
	AssignUtil.localIdsToPredefinedConfigurations(predefinedConfigurations, predefinedProductInfo);

	const selectedConfiguration = UrlService.getVpcOrSprId();
	const dataHandler = new DataHandler();
	const params = UrlService.getApplicationParams();
	const product = selectedConfiguration
		? await dataHandler.getProductFromService(data.state.settings.settings, [
				{ type: params.cover ? 'ART' : 'SPR', id: selectedConfiguration },
		  ])
		: null;
	const id =
		product && product.length > 0
			? product[0].content?.itemNoGlobal ?? product[0].content?.itemNoLocal
			: selectedConfiguration;
	predefinedConfigurations = predefinedConfigurations.filter((configuration) => {
		return (
			configuration.id === id ||
			configuration.localId === id ||
			configuration.coverId === id ||
			galleryItems.find((galleryItem) => configuration.id === galleryItem.productItem.id)
		);
	});
	return { predefinedConfigurations, allPredefinedConfigurations };
}

async function handleProductVariants(
	data: IDataFetch,
	missingProducts: MissingProductItem[],
	predefinedConfigurations: PredefinedConfiguration[],
	allPredefinedConfigurations: PredefinedConfiguration[],
	galleryItems: GalleryItem[]
) {
	const selectedConfiguration = UrlService.getVpcOrSprId();
	const dataHandler = new DataHandler();
	const params = UrlService.getApplicationParams();
	const product = selectedConfiguration
		? await dataHandler.getProductFromService(data.state.settings.settings, [
				{ type: params.cover ? 'ART' : 'SPR', id: selectedConfiguration },
		  ])
		: null;
	const id =
		product && product.length > 0
			? product[0].content?.itemNoGlobal ?? product[0].content?.itemNoLocal
			: selectedConfiguration;
	const productVariantParser = new ProductVariantParser();
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const settings = (data.state as any).settings.settings as Settings;
	const productVariantsPredefined = await productVariantParser.parse(
		data.products,
		missingProducts,
		data.rangeSettings,
		predefinedConfigurations,
		allPredefinedConfigurations,
		data.fetchedProducts,
		settings
	);
	/* Add product variants and cover configuration to predefined configurations */
	predefinedConfigurations = allPredefinedConfigurations.filter((configuration) => {
		return (
			configuration.id === id ||
			configuration.localId === id ||
			configuration.coverId === id ||
			galleryItems.find((galleryItem) => configuration.id === galleryItem.productItem.id) ||
			productVariantsPredefined.find((item) => configuration.id === item)
		);
	});
	return predefinedConfigurations;
}

async function handleDataFetch(data: IDataFetch) {
	const galleryItemsParser = new GalleryItemsParser();
	const galleryItems = await galleryItemsParser.parse(
		data.config,
		data.useLocalIds,
		data.fetchedProducts,
		data.state
	);

	const { predefinedConfigurations, allPredefinedConfigurations } = await handlePredefined(
		data,
		galleryItems
	);
	const selectedConfiguration = UrlService.getVpcOrSprId();

	const missingProductsParser = new MissingProductsParser();
	const { missingProducts, missingArmrestCovers } = missingProductsParser.parse(
		data.products,
		data.productInfo,
		!data.rangeSettings.addArmrestsAutomatically
	);

	const coverParser = new CoverParser();
	const { currentCover, coverItems } = await coverParser.parse(
		data.productInfo,
		data.products,
		missingProducts,
		missingArmrestCovers,
		allPredefinedConfigurations,
		selectedConfiguration,
		data.state,
		data.rangeSettings
	);
	if (currentCover) {
		const updatedSettings = {
			...data.state.settings.settings,
			applicationNameTranslated: data.rangeSettings.rangeName as string,
		};
		await handleCovers(data, updatedSettings, coverItems, currentCover);
		await handleConfig(data, galleryItems, missingProducts);
		await handleSettings(
			data,
			missingProducts,
			predefinedConfigurations,
			allPredefinedConfigurations,
			galleryItems,
			updatedSettings
		);
		data.dispatch(setFakeData(null));
		data.dispatch(
			setModels({ sofaGroups: parseGroups(data.config), sofaModels: parseModelData(data.config) })
		);
	} else {
		setTimeout(function () {
			if (data.state.application.isInUpptackaPlatform) {
				window.parent.postMessage(
					{
						method: 'upptacka_missing_product_error',
					},
					'*'
				);
			} else {
				data.dispatch(noArticlesInplanner());
			}
		}, 1);
	}
}

async function handleConfig(
	data: IDataFetch,
	galleryItems: GalleryItem[],
	missingProducts: MissingProductItem[]
) {
	const dataHandler = new DataHandler();
	const galleryShoppingProducts = await dataHandler.getShoppingGalleryItems(
		data.state.settings.settings,
		galleryItems,
		data.state.localization.storeId
	);

	const params = UrlService.getApplicationParams();
	data.dispatch(
		fetchConfigSuccess(
			params.fakeData ? data.productInfo : null,
			data.products,
			galleryItems,
			missingProducts,
			galleryShoppingProducts
		)
	);
}

async function handleCovers(
	data: IDataFetch,
	updatedSettings: Settings,
	coverItems: CoverItem[],
	currentCover: CoverItem
) {
	const dataHandler = new DataHandler();
	const coverOnlyItems = await dataHandler.getCoverOnlyItems(updatedSettings, coverItems);

	AssignUtil.armrests(data.dispatch, data.products, coverItems);
	data.dispatch(setSelectedCover(currentCover, 'product-intro-gallery'));
	data.dispatch(setCoverItems(coverItems));
	data.dispatch(setCoverOnlyItems(coverOnlyItems));
}

async function handleSettings(
	data: IDataFetch,
	missingProducts: MissingProductItem[],
	predefinedConfigurations: PredefinedConfiguration[],
	allPredefinedConfigurations: PredefinedConfiguration[],
	galleryItems: GalleryItem[],
	updatedSettings: Settings
) {
	const predefinedConfigurationVariant = await handleProductVariants(
		data,
		missingProducts,
		predefinedConfigurations,
		allPredefinedConfigurations,
		galleryItems
	);
	data.dispatch(
		setSettings({
			settings: updatedSettings,
			rangeSettings: data.rangeSettings,
			predefinedConfigurations: predefinedConfigurationVariant,
		})
	);
}

export function setCoverItem(coverItem: CoverItem, coverUpdated: boolean): ThunkResult {
	return async (dispatch): Promise<void> => {
		if (!coverUpdated) {
			dispatch(setSelectedCover(coverItem, 'product-intro-gallery'));
		}
		dispatch(setModuleSwiperItems());
	};
}

export function setModuleSwiperItems(): ThunkResult {
	return async (dispatch, getState): Promise<void> => {
		const state = getState();
		const { fakeData } = state.application;
		const { settings, rangeSettings } = state.settings;
		const { platform } = state.deviceInfo;
		const { localization, storeId } = state.localization;
		const { products } = state.products;
		const locale = state.localization.locale as string;
		const { selectedCover } = state.cover;
		const storeName = storeId
			? await getStoreName(settings, platform === 'kiosk' ? UrlService.getStoreId() : storeId)
			: '';

		if (selectedCover) {
			const productInfoParser = new ProductInfoParser();
			const productInfo: ProductInfo = await productInfoParser.parse(
				products,
				rangeSettings as RangeSettings,
				locale,
				fakeData,
				rangeSettings?.useLocalIds &&
					rangeSettings.useLocalIds.includes(localeService.getCountryCode())
					? true
					: false,
				null,
				settings
			);
			const moduleSwiperItems = await parseModuleSwiperItems(
				products,
				productInfo,
				selectedCover,
				rangeSettings as RangeSettings,
				platform,
				localization ? localization.useMetricMeasures : true,
				settings,
				storeId,
				storeName
			);
			dispatch(setItemsInModuleSwiper(moduleSwiperItems));
			dispatch(setModuleItemsChanged(true));
		}
	};
}

async function getStoreName(settings: Settings, storeId: string): Promise<string> {
	const storeService = new StoreService({
		locale: settings.locale,
		localizationPrefixApplication: settings.localizationPrefixApplication,
		localizationEndpoints: settings.localizationEndpoints,
		consumer: settings.consumer,
		contract: settings.contract,
	});

	const stores = await storeService.getStores();
	return stores?.find((store) => store.id === storeId)?.name || '';
}
